import { Component, OnInit, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { BillingService } from '@app/shared/services/billing.service'
import { StripeAuBankAccountElement, StripeAuBankAccountElementOptions } from '@stripe/stripe-js'
import { StripeInstance } from 'ngx-stripe'
import { finalize, switchMap, tap } from 'rxjs'

@Component({
  selector: 'lin-add-becs-form',
  templateUrl: './add-becs-form.component.html',
  styleUrls: ['./add-becs-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AddBecsFormComponent implements OnInit {
  @Input() stripe: StripeInstance
  @Input() clientSecret: string
  @Input() paymentMethodCreating = false
  @Output() savePaymentMethod = new EventEmitter()

  form: FormGroup
  isSubmitting = false
  error: string
  auBankAccount: StripeAuBankAccountElement
  // Custom styling can be passed to options when creating an Element
  private style = {
    base: {
      color: '#32325d',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
      ':-webkit-autofill': {
        color: '#32325d',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
      ':-webkit-autofill': {
        color: '#fa755a',
      },
    },
  }

  private options: StripeAuBankAccountElementOptions = {
    style: this.style,
    disabled: false,
    hideIcon: false,
    iconStyle: 'default', // or "solid"
  }

  constructor(
    private fb: FormBuilder,
    private billingService: BillingService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required]],
      email: ['', [Validators.required]],
    })
    this.stripe
      .elements()
      .pipe(
        tap((elements) => {
          // Create an instance of the auBankAccount Element.
          this.auBankAccount = elements.create('auBankAccount', this.options)
          // Add an instance of the auBankAccount Element into
          // the `au-bank-account-element` <div>.
          this.auBankAccount.mount('#au-bank-account-element')
        }),
      )
      .subscribe()
  }

  onSubmit() {
    this.isSubmitting = true

    const { name, email } = this.form.value as { name: string; email: string }

    this.stripe
      .confirmAuBecsDebitSetup(this.clientSecret, {
        payment_method: {
          au_becs_debit: this.auBankAccount,
          billing_details: {
            name,
            email,
          },
        },
      })
      .pipe(
        tap((result) => {
          if (result.error) {
            this.error = result.error.message
          } else {
            this.clearForm()
            this.savePaymentMethod.emit(<string>result.setupIntent.payment_method)
          }
        }),
        switchMap(({ setupIntent }) => this.billingService.sendSetupIntent(setupIntent.id)),
        finalize(() => (this.isSubmitting = false)),
      )
      .subscribe()
  }

  get isDisabled(): boolean {
    return !!this.form.invalid || !this.stripe
  }

  private clearForm(): void {
    this.form.reset()
    this.form.markAsUntouched()
    this.form.get('name').setErrors(null)
    this.form.get('email').setErrors(null)

    this.auBankAccount.clear()
  }
}
